/**
 * mc-hero-carousel.js — Manifesto (task 0004)
 *
 * Custom element <mc-hero-carousel> — carrossel de ate 3 slides na primeira
 * dobra da home (sections/hero-carousel-manifesto.liquid). Vanilla JS, no
 * padrao dos outros componentes do tema (mc-cart-discount.js,
 * mc-shipping-calc.js): sem Alpine, tudo via data-* / dataset. Carrega com
 * `defer` (Theme Check marca <script> bloqueante como [error]
 * ParserBlockingScript, entao nao da pra upgradar o elemento de forma
 * sincrona antes do paint).
 *
 * Por isso o CSS de assets/mc-home.css JA nasce no estado "final" (grid
 * empilhado / crossfade, altura unica = altura do hero atual, CLS ~ 0)
 * independente de JS — nao existe uma classe "enhanced" que este script
 * precise adicionar para o visual ficar correto. Este arquivo so liga
 * COMPORTAMENTO: dots, setas, autoplay (DP4), swipe (DP5), teclado
 * (setas esquerda/direita), aria-hidden/tabindex dinamicos por slide e a
 * telemetria do CTA (DP11).
 *
 * Progressive enhancement de verdade (DP10 — sem JS, todos os slides
 * empilhados e visiveis, links funcionando): como `defer` nao garante rodar
 * antes do paint, o fallback sem-JS e resolvido via <noscript><style> na
 * propria section (unico mecanismo sem corrida de tempo) — nao depende
 * deste arquivo em nenhuma hipotese.
 *
 * DP3: com <=1 slide valido, nenhum controle/autoplay/swipe/teclado e
 * instalado — o elemento so mantem o listener de telemetria do CTA (DP11),
 * que se aplica tambem ao hero estatico de slide unico.
 */
(function () {
  'use strict';

  if (customElements.get('mc-hero-carousel')) return;

  var SWIPE_THRESHOLD = 40;

  customElements.define(
    'mc-hero-carousel',
    class McHeroCarousel extends HTMLElement {
      connectedCallback() {
        if (this._mcInitialized) return;
        this._mcInitialized = true;

        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-mc-hero-slide]'));
        this.count = this.slides.length;
        this.current = 0;

        // Telemetria do CTA (DP11) — instalada sempre, mesmo no hero estatico (1 slide).
        this.onCtaClick = this.onCtaClick.bind(this);
        this.addEventListener('click', this.onCtaClick);

        if (this.count === 0) return;

        this.syncActiveState();

        if (this.count <= 1) return; // DP3 — sem dots/setas/autoplay/swipe/teclado

        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-mc-hero-dot]'));
        this.prevBtn = this.querySelector('[data-mc-hero-prev]');
        this.nextBtn = this.querySelector('[data-mc-hero-next]');
        this.live = this.querySelector('[data-mc-hero-live]');
        this.strSlideOf = this.dataset.strSlideOf || 'Slide %CURRENT% de %TOTAL%';

        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.interval = parseInt(this.dataset.interval, 10) || 5000;
        this.reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.hovering = false;
        this.focused = false;
        this.timer = null;
        this.touchStartX = null;
        this.touchStartY = null;
        this.suppressClick = false;
        this.userPaused = false;

        this.onDotClick = this.onDotClick.bind(this);
        this.onPrev = this.onPrev.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onPointerCancel = this.onPointerCancel.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onMouseEnter = this.handleEnter.bind(this);
        this.onMouseLeave = this.handleLeave.bind(this);
        this.onFocusIn = this.handleFocusIn.bind(this);
        this.onFocusOut = this.handleFocusOut.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);

        this.dots.forEach(
          function (dot) {
            dot.addEventListener('click', this.onDotClick);
          }.bind(this)
        );
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointerup', this.onPointerUp);
        this.addEventListener('pointercancel', this.onPointerCancel);
        this.addEventListener('click', this.onClickCapture, true);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        // AJ-6 — botao play/pause (DP-b). So existe no markup quando
        // section.settings.autoplay esta ligado; sem ele, autoplay nao tem
        // controle de pausa persistente algum alem de hover/foco/aba-oculta.
        this.playPauseBtn = this.querySelector('[data-mc-hero-playpause]');
        if (this.playPauseBtn) {
          this.strPause = this.playPauseBtn.dataset.strPause || 'Pausar apresentacao automatica';
          this.strPlay = this.playPauseBtn.dataset.strPlay || 'Reproduzir apresentacao automatica';
          this.iconPause = this.querySelector('[data-mc-hero-icon-pause]');
          this.iconPlay = this.querySelector('[data-mc-hero-icon-play]');
          this.onPlayPause = this.onPlayPause.bind(this);
          this.playPauseBtn.addEventListener('click', this.onPlayPause);
        }

        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (this.onPointerCancel) this.removeEventListener('pointercancel', this.onPointerCancel);
        if (this.onClickCapture) this.removeEventListener('click', this.onClickCapture, true);
        if (this.playPauseBtn && this.onPlayPause) this.playPauseBtn.removeEventListener('click', this.onPlayPause);
      }

      // --- Estado / render ------------------------------------------------

      syncActiveState(userInitiated) {
        var self = this;
        this.slides.forEach(function (slide, i) {
          var active = i === self.current;
          slide.classList.toggle('is-active', active);
          if (active) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');

          var focusable = slide.querySelectorAll('a[href], button');
          focusable.forEach(function (el) {
            if (active) el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', '-1');
          });
        });

        if (this.dots) {
          this.dots.forEach(function (dot, i) {
            var active = i === self.current;
            dot.classList.toggle('is-active', active);
            if (active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }

        // AJ-2 — o live region so anuncia navegacao iniciada pelo usuario
        // (dot/seta/teclado/swipe). O tick do autoplay chama goTo(..., false)
        // e nao deve produzir anuncio nenhum no leitor de tela (WCAG 4.1.3).
        if (this.live && this.count > 1 && userInitiated) {
          this.live.textContent = this.strSlideOf
            .replace('%CURRENT%', String(this.current + 1))
            .replace('%TOTAL%', String(this.count));
        }
      }

      goTo(index, userInitiated) {
        var next = ((index % this.count) + this.count) % this.count;
        if (next === this.current) return;
        this.current = next;
        this.syncActiveState(userInitiated);
      }

      // --- Controles --------------------------------------------------------

      onDotClick(e) {
        var idx = this.dots.indexOf(e.currentTarget);
        if (idx === -1) return;
        this.goTo(idx, true);
        this.restartAutoplay();
      }

      onPrev() {
        this.goTo(this.current - 1, true);
        this.restartAutoplay();
      }

      onNext() {
        this.goTo(this.current + 1, true);
        this.restartAutoplay();
      }

      onKeydown(e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goTo(this.current - 1, true);
          this.restartAutoplay();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goTo(this.current + 1, true);
          this.restartAutoplay();
        }
      }

      // AJ-4 — swipe robusto: exige dominancia horizontal (|dx| > |dy|) alem
      // do limiar de distancia, e suprime o click subsequente (link de fundo/
      // CTA + telemetria) quando o gesto trocou de slide. Scroll vertical
      // (|dy| >= |dx|) nunca troca slide — ver touch-action: pan-y no CSS.
      onPointerDown(e) {
        if (e.pointerType === 'mouse') return;
        this.touchStartX = e.clientX;
        this.touchStartY = e.clientY;
        this.suppressClick = false;
      }

      onPointerUp(e) {
        if (this.touchStartX === null || this.touchStartX === undefined) return;
        var dx = e.clientX - this.touchStartX;
        var dy = e.clientY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(dx) <= Math.abs(dy)) return; // gesto vertical — scroll nativo
        if (Math.abs(dx) <= SWIPE_THRESHOLD) return; // curto demais

        if (dx > 0) {
          this.goTo(this.current - 1, true);
        } else {
          this.goTo(this.current + 1, true);
        }
        this.restartAutoplay();

        this.suppressClick = true;
        var self = this;
        window.setTimeout(function () {
          self.suppressClick = false;
        }, 400);
      }

      onPointerCancel() {
        this.touchStartX = null;
        this.touchStartY = null;
      }

      // Fase de CAPTURA no proprio elemento: se o pointerup imediatamente
      // anterior trocou o slide por swipe, este handler roda ANTES do click
      // chegar ao alvo (link de fundo/CTA) e ANTES do onCtaClick (bubble) —
      // preventDefault + stopPropagation impedem navegacao e telemetria
      // indevida no click pos-swipe.
      onClickCapture(e) {
        if (this.suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          this.suppressClick = false;
        }
      }

      // --- Telemetria (DP11) ------------------------------------------------

      onCtaClick(e) {
        var link = e.target.closest('[data-mc-hero-cta]');
        if (!link) return;
        var index = parseInt(link.getAttribute('data-slide-index'), 10);
        if (!index) index = this.current + 1;
        var headline = link.getAttribute('data-slide-headline') || '';

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'hero_slide_cta_click', slide_index: index, slide_headline: headline });
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'hero_slide_cta_click', { slide_index: index, slide_headline: headline });
        }
      }

      // --- Play/pause (AJ-6, DP-b) ---------------------------------------
      // Pausa PERSISTENTE por acao do usuario: uma vez pausado, hover-leave/
      // focus-out/visibilitychange/dot/seta/teclado/swipe NUNCA reativam o
      // autoplay sozinhos (startAutoplay() retorna cedo enquanto userPaused).
      // So o clique de "reproduzir" tira o estado pausado.

      onPlayPause() {
        if (this.userPaused) {
          this.userPaused = false;
          this.startAutoplay();
        } else {
          this.userPaused = true;
          this.stopAutoplay();
        }
        this.updatePlayPauseUI();
      }

      updatePlayPauseUI() {
        if (!this.playPauseBtn) return;
        if (this.userPaused) {
          if (this.iconPause) this.iconPause.setAttribute('hidden', '');
          if (this.iconPlay) this.iconPlay.removeAttribute('hidden');
          this.playPauseBtn.setAttribute('aria-label', this.strPlay);
        } else {
          if (this.iconPlay) this.iconPlay.setAttribute('hidden', '');
          if (this.iconPause) this.iconPause.removeAttribute('hidden');
          this.playPauseBtn.setAttribute('aria-label', this.strPause);
        }
      }

      // --- Autoplay (DP4) -----------------------------------------------

      handleEnter() {
        this.hovering = true;
        this.stopAutoplay();
      }
      handleLeave() {
        this.hovering = false;
        this.maybeResumeAutoplay();
      }
      handleFocusIn() {
        this.focused = true;
        this.stopAutoplay();
      }
      handleFocusOut() {
        this.focused = false;
        this.maybeResumeAutoplay();
      }
      handleVisibilityChange() {
        if (document.hidden) this.stopAutoplay();
        else this.maybeResumeAutoplay();
      }
      maybeResumeAutoplay() {
        if (!this.hovering && !this.focused) this.startAutoplay();
      }

      startAutoplay() {
        if (this.userPaused) return;
        if (!this.autoplayEnabled || this.count <= 1) return;
        if (this.reducedMotionMql && this.reducedMotionMql.matches) return;
        if (document.hidden || this.hovering || this.focused) return;
        this.stopAutoplay();
        var self = this;
        this.timer = window.setInterval(function () {
          self.goTo(self.current + 1, false);
        }, this.interval);
      }

      stopAutoplay() {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }

      restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }
  );
})();
